if (!customElements.get('trust-indicators-slider')) {


  class TrustIndicatorsSlider extends HTMLElement {
    static get observedAttributes() {
      return ['data-swiper-options'];
    }

    constructor() {
      super();

      this.slider = null;
      this.swiperOptions = {};
      this.initSlider(this.getSwiperOptions());
      this.handleResize = this.handleResize.bind(this);
      window.addEventListener('resize', this.handleResize);
    }

    getSwiperOptions() {
      return JSON.parse(this.getAttribute('data-swiper-options')) || {};
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name !== 'data-swiper-options' || oldValue === newValue) {
        return;
      }

      this.destroySlider();
      this.initSlider(this.getSwiperOptions());
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.handleResize);
      this.destroySlider();
    }

    destroySlider() {
      if (this.slider) {
        this.slider.destroy(true, true);
        this.slider = null;
      }
    }

    isDesktopViewport() {
      return window.matchMedia('(min-width: 750px)').matches;
    }

    syncEnabledState() {
      if (!this.slider) {
        return;
      }

      const sliderMobileEnabled = this.swiperOptions.sliderMobileEnabled !== false;
      const sliderDesktopEnabled = this.swiperOptions.sliderDesktopEnabled !== false;
      const isDesktop = this.isDesktopViewport();

      if (sliderMobileEnabled && !sliderDesktopEnabled) {
        if (isDesktop) {
          this.slider.disable();
        } else {
          this.slider.enable();
          this.slider.update();
        }
        return;
      }

      if (!sliderMobileEnabled && sliderDesktopEnabled) {
        if (isDesktop) {
          this.slider.enable();
          this.slider.update();
        } else {
          this.slider.disable();
        }
        return;
      }

      if (sliderMobileEnabled || sliderDesktopEnabled) {
        this.slider.enable();
        this.slider.update();
      }
    }

    handleResize() {
      this.syncEnabledState();
    }
    
    initSlider(swiperOptions) {
      this.swiperOptions = swiperOptions;

      // Create unique IDs for this carousel instance
      const carouselId = `carousel-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      this.setAttribute('data-carousel-id', carouselId);
      
      // Find navigation buttons - try multiple approaches
      let nextBtn = this.querySelector('.swiper-button--next');
      let prevBtn = this.querySelector('.swiper-button--prev');
      
      // If not found inside, try parent section
      if (!nextBtn || !prevBtn) {
        const section = this.closest('section') || this.closest('[data-section-id]');
        if (section) {
          nextBtn = nextBtn || section.querySelector('.swiper-button--next');
          prevBtn = prevBtn || section.querySelector('.swiper-button--prev');
        }
      }
      
      // Only assign to this carousel if buttons are not already assigned to another carousel
      if (nextBtn && !nextBtn.hasAttribute('data-carousel-id')) {
        nextBtn.setAttribute('data-carousel-id', carouselId);
        nextBtn.id = `next-${carouselId}`;
      }
      if (prevBtn && !prevBtn.hasAttribute('data-carousel-id')) {
        prevBtn.setAttribute('data-carousel-id', carouselId);
        prevBtn.id = `prev-${carouselId}`;
      }

      const sliderMobileEnabled = swiperOptions.sliderMobileEnabled !== false;
      const sliderDesktopEnabled = swiperOptions.sliderDesktopEnabled !== false;

      this.classList.toggle(
        'section-trust-indicators__swiper--desktop-carousel',
        sliderDesktopEnabled
      );
      this.classList.toggle(
        'section-trust-indicators__swiper--mobile-carousel',
        sliderMobileEnabled
      );

      const rawMobile = Math.floor(Number(swiperOptions.slidesPerViewMobile));
      const mobileSlidesWide = Number.isFinite(rawMobile)
        ? Math.min(3, Math.max(1, rawMobile))
        : 1;
      const rawDesktop = Math.floor(Number(swiperOptions.slidesPerViewDesktop));
      const desktopSlides = Number.isFinite(rawDesktop)
        ? Math.min(6, Math.max(1, rawDesktop))
        : 3;
      // ≤480px: fractional slidesPerView (peek); 481–749px: whole columns
      const mobileSlidesNarrow =
        mobileSlidesWide === 1 ? 1.5 : mobileSlidesWide === 2 ? 2.5 : 3.5;

      const breakpoints = {};

      if (sliderMobileEnabled) {
        breakpoints[481] = {
          enabled: true,
          slidesPerView: mobileSlidesWide,
          spaceBetween: swiperOptions.spaceBetweenMobile || 2
        };
      }

      if (sliderDesktopEnabled) {
        breakpoints[750] = {
          enabled: true,
          spaceBetween: swiperOptions.spaceBetweenDesktop || 2,
          slidesPerView: desktopSlides,
          loop: false
        };
      } else if (sliderMobileEnabled) {
        breakpoints[750] = {
          enabled: false,
          spaceBetween: swiperOptions.spaceBetweenDesktop || 2,
          slidesPerView: desktopSlides,
          loop: false
        };
      }

      const shouldStartEnabled = sliderMobileEnabled
        ? !this.isDesktopViewport() || sliderDesktopEnabled
        : sliderDesktopEnabled && this.isDesktopViewport();

      this.slider = new Swiper(this, {
        enabled: shouldStartEnabled,
        slidesPerView: mobileSlidesNarrow,
        observer: true,
        observeParents: true,
        watchOverflow: true,
        navigation: {
          nextEl: (nextBtn && nextBtn.getAttribute('data-carousel-id') === carouselId) ? `#next-${carouselId}` : null,
          prevEl: (prevBtn && prevBtn.getAttribute('data-carousel-id') === carouselId) ? `#prev-${carouselId}` : null,
        },
        spaceBetween: swiperOptions.spaceBetweenMobile || 2,
        breakpoints,
        on: {
          init: () => {
            this.syncEnabledState();
          },
          breakpoint: () => {
            this.syncEnabledState();
          }
        }
      });
    }
  }

  customElements.define('trust-indicators-slider', TrustIndicatorsSlider);
}
